import { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/format';
import TradeCalculator, { TRADES } from '../components/TradeCalculator';
import toast from 'react-hot-toast';

const STAGES = [
  { key: 'sold',       label: 'Sold',            icon: '🤝', desc: 'Contract signed' },
  { key: 'production', label: 'In Production',   icon: '🔨', desc: 'Work in progress' },
  { key: 'inspection', label: 'Inspection/Cert', icon: '📋', desc: 'Cert & permits' },
  { key: 'funding',    label: 'Funding',          icon: '💰', desc: 'Payment collection' },
  { key: 'complete',   label: 'Complete',         icon: '✅', desc: 'Job closed & funded' },
];

function getStage(job) {
  const prod = (job.prod_status || '').toLowerCase();
  const sales = (job.sales_status || '').toLowerCase();
  if (sales === 'cancelled') return -1;
  if (prod.includes('funded') || prod.includes('complete') || prod.includes('closed') || sales === 'closed') return 4;
  if (prod.includes('funding') || prod.includes('clear to collect')) return 3;
  if (prod.includes('cert') || prod.includes('permit')) return 2;
  if (prod.includes('on-site') || prod.includes('schedule confirmed') || prod.includes('need to schedule') || prod.includes('remeasure') || prod.includes('contractor')) return 1;
  return 0;
}

function isFundedClosed(job) {
  const prod = (job.prod_status || '').toLowerCase();
  const sales = (job.sales_status || '').toLowerCase();
  return sales === 'closed' || sales === 'funded' || prod.includes('funded') || prod.includes('complete') || prod === 'closed';
}

function jobValue(job) {
  return (parseFloat(job.contract_amt) || 0) + (parseFloat(job.change_order_amt) || 0);
}

function getCommissionRate(name, type) {
  const n = (name || '').toLowerCase().trim();
  if (n === 'alfredo' || n === 'jp' || n === 'carlos') return 0.03;
  if (type === 'telemarketer') return 0.0175;
  if (n === 'kaleb' || n === 'caleb') return 0.015;
  return 0.0125;
}

function isManager(name) {
  const n = (name || '').toLowerCase().trim();
  return ['alfredo', 'jp', 'carlos'].includes(n);
}

function fmt(n) {
  return '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Timeline({ job }) {
  const currentStage = getStage(job);
  if (currentStage === -1) {
    return <div style={{ padding: '12px 0', color: '#c62828', fontWeight: 600, fontSize: 13 }}>✕ Cancelled</div>;
  }
  return (
    <div style={{ padding: '16px 0 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 20, left: 20, right: 20, height: 3, background: '#e0e0e0', zIndex: 0 }} />
        <div style={{
          position: 'absolute', top: 20, left: 20, height: 3,
          background: 'linear-gradient(to right, #1a355f, #4b72b0)',
          width: currentStage === 0 ? '0%' : `${(currentStage / (STAGES.length - 1)) * (100 - (40 / (STAGES.length - 1)))}%`,
          zIndex: 1, transition: 'width 0.5s ease',
        }} />
        {STAGES.map((stage, idx) => {
          const done = idx < currentStage;
          const active = idx === currentStage;
          return (
            <div key={stage.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 2 }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: done ? '#1a355f' : active ? '#F57F17' : '#fff',
                border: `3px solid ${done ? '#1a355f' : active ? '#F57F17' : '#ddd'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: done ? 16 : 18,
                boxShadow: active ? '0 0 0 4px rgba(245,127,23,0.2)' : 'none',
              }}>
                {done ? '✓' : stage.icon}
              </div>
              <div style={{ marginTop: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: active ? 700 : done ? 600 : 400, color: done ? '#1a355f' : active ? '#F57F17' : '#aaa', whiteSpace: 'nowrap' }}>
                  {stage.label}
                </div>
                <div style={{ fontSize: 10, color: '#bbb', marginTop: 2 }}>{stage.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 14, padding: '8px 12px', background: '#f9f9f9', borderRadius: 6, fontSize: 12, color: '#555', display: 'flex', gap: 10 }}>
        <span style={{ color: '#F57F17', fontWeight: 700 }}>Current:</span>
        <span>{job.prod_status || job.sales_status || 'Active'}</span>
        {job.start_date && <span style={{ color: '#aaa' }}>· Start: {job.start_date}</span>}
        {job.end_date && <span style={{ color: '#aaa' }}>· End: {job.end_date}</span>}
      </div>
    </div>
  );
}

const STATUS_COLOR = {
  'Active': '#1a355f', 'Funded': '#1565C0', 'Funding': '#1565C0',
  'Cancelled': '#c62828', 'Closed': '#555', 'Hold': '#E65100', 'PTD': '#6a1a9a',
};

export default function MyJobs() {
  const { username, staff_name } = useAuth();
  const [activeTab, setActiveTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [payrollLoading, setPayrollLoading] = useState(false);

  const displayName = staff_name || username;
  const rate = getCommissionRate(displayName);
  const isManagerRole = ['alfredo', 'jp'].includes((displayName || '').toLowerCase().trim());
  const [overrideData, setOverrideData] = useState(null);
  const [staffRole, setStaffRole] = useState(null);
  const [calcTrade, setCalcTrade] = useState('painting');

  useEffect(() => {
    api.get('/canvasser-commission/my-role')
      .then(r => setStaffRole(r.data?.staff_role || null))
      .catch(() => setStaffRole(null));
  }, []);

  const isSalesRep = (staffRole || '').toLowerCase() === 'sales rep';

  useEffect(() => {
    api.get('/auth/myjobs').then(res => {
      setJobs(Array.isArray(res.data) ? res.data : []);
    }).catch(() => toast.error('Failed to load jobs'))
    .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'commission') {
      loadPayrollHistory();
      if (isManagerRole) loadOverrideData();
    }
  }, [activeTab]);

  async function loadOverrideData() {
    try {
      const res = await api.get(`/canvasser-commission/team-override/${displayName}`);
      setOverrideData(res.data);
    } catch {}
  }

  async function loadPayrollHistory() {
    setPayrollLoading(true);
    try {
      const res = await api.get('/canvasser-commission/periods');
      const periods = Array.isArray(res.data) ? res.data : [];
      // Load entries for each period
      const details = await Promise.all(
        periods.map(async p => {
          const r = await api.get(`/canvasser-commission/period/${p.pay_date}`);
          const entries = Array.isArray(r.data) ? r.data.filter(e => e.canvasser?.toLowerCase() === displayName?.toLowerCase() && !e.override_for) : [];
          return { ...p, entries, myTotal: entries.reduce((s, e) => s + e.commission_amt, 0) };
        })
      );
      setPayrollHistory(details.filter(p => p.entries.length > 0));
    } catch {} finally { setPayrollLoading(false); }
  }

  const filtered = jobs.filter(j => {
    if (filter === 'active') return !['cancelled','closed'].includes((j.sales_status||'').toLowerCase());
    if (filter === 'closed') return ['closed','funded','job complete & funded'].includes((j.prod_status||'').toLowerCase()) || (j.sales_status||'').toLowerCase() === 'closed';
    if (filter === 'cancelled') return (j.sales_status||'').toLowerCase() === 'cancelled';
    return true;
  });

  const totalContract = jobs.filter(j => (j.sales_status||'').toLowerCase() !== 'cancelled')
    .reduce((s, j) => s + parseFloat(j.change_order_amt || j.contract_amt || 0), 0);

  // Commission: canvassers/telemarketers earn on the first TWO jobs per client.
  // Rank the user's own jobs within each client group (base PO), suffix ascending.
  const parsePO = po => {
    const s = (po || '').trim();
    const m = s.match(/^(.*)-(\d+)$/);
    return m ? { base: m[1].toLowerCase(), suffix: parseInt(m[2], 10) } : { base: s.toLowerCase(), suffix: 1 };
  };
  const rankMap = (() => {
    const groups = {};
    jobs.forEach(j => { const { base, suffix } = parsePO(j.po_number); (groups[base] = groups[base] || []).push({ id: j.id, suffix }); });
    const m = {};
    Object.values(groups).forEach(arr => { arr.sort((a, b) => a.suffix - b.suffix); arr.forEach((g, i) => { m[g.id] = i + 1; }); });
    return m;
  })();
  const withinFirstTwo = j => (rankMap[j.id] || 1) <= 2;

  const completedJobs = jobs.filter(j => isFundedClosed(j) && withinFirstTwo(j));
  const paidJobIds = payrollHistory.flatMap(p => p.entries.map(e => e.job_id));
  const pendingJobs = completedJobs.filter(j => !paidJobIds.includes(j.id));
  const totalEarned = completedJobs.reduce((s, j) => s + jobValue(j) * rate, 0);
  const totalPaid = payrollHistory.reduce((s, p) => s + p.myTotal, 0);
  const totalPending = pendingJobs.reduce((s, j) => s + jobValue(j) * rate, 0);

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <>
      <div className="page-header">
        <h2>My Jobs</h2>
        <div style={{ fontSize: 13, color: '#555' }}>
          <strong>{displayName}</strong> · {jobs.length} total jobs
        </div>
      </div>

      <div className="page-body">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #eee' }}>
          {[['jobs', '💼 My Jobs'], ['commission', '💵 My Commission'], ...(isSalesRep ? [['calculator', '🧮 Calculator']] : [])].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: activeTab === key ? 700 : 400,
                color: activeTab === key ? '#0d1f3b' : '#888', borderBottom: activeTab === key ? '2px solid #0d1f3b' : '2px solid transparent',
                marginBottom: -2, fontSize: 14 }}>
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'calculator' && isSalesRep && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 8 }}>Job type:</label>
              <select value={calcTrade} onChange={e => setCalcTrade(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #ccc', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#0d1f3b' }}>
                {Object.entries(TRADES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <TradeCalculator trade={calcTrade} />
          </div>
        )}

        {activeTab === 'jobs' && (
          <>
            {/* Summary */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <SummaryCard label="Total Jobs" value={jobs.length} color="#0d1f3b" />
              <SummaryCard label="Active" value={jobs.filter(j => !['cancelled','closed'].includes((j.sales_status||'').toLowerCase())).length} color="#1a355f" />
              <SummaryCard label="Contract Value" value={'$' + totalContract.toLocaleString('en-US', { maximumFractionDigits: 0 })} color="#F57F17" />
              <SummaryCard label="Cancelled" value={jobs.filter(j => (j.sales_status||'').toLowerCase() === 'cancelled').length} color="#c62828" />
            </div>

            {/* Filter */}
            <div className="filter-bar" style={{ marginBottom: 16 }}>
              {[['all','All Jobs'],['active','Active'],['closed','Closed'],['cancelled','Cancelled']].map(([key, label]) => (
                <button key={key} className={`filter-btn${filter === key ? ' active' : ''}`} onClick={() => setFilter(key)}>{label}</button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="empty-state"><p>No jobs found</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {filtered.map(job => {
                  const isOpen = expanded === job.id;
                  const isCancelled = (job.sales_status||'').toLowerCase() === 'cancelled';
                  const svcList = [job.svc1, job.svc2, job.svc3, job.svc4].filter(Boolean).join(', ');
                  return (
                    <div key={job.id} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', borderLeft: `4px solid ${isCancelled ? '#ef9a9a' : '#1a355f'}` }}>
                      <div onClick={() => setExpanded(isOpen ? null : job.id)}
                        style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ fontWeight: 700, fontSize: 15, color: '#0d1f3b' }}>{job.customer_name}</div>
                          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{job.po_number} · {job.address || ''} {job.city || ''}</div>
                          {svcList && <div style={{ fontSize: 12, color: '#1565C0', marginTop: 2 }}>{svcList}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ background: (STATUS_COLOR[job.sales_status] || '#888') + '22', color: STATUS_COLOR[job.sales_status] || '#888', border: '1px solid ' + (STATUS_COLOR[job.sales_status] || '#888') + '44', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{job.sales_status || '—'}</span>
                          <div style={{ fontWeight: 700, color: '#0d1f3b', fontSize: 14 }}>{formatCurrency(job.change_order_amt || job.contract_amt)}</div>
                        </div>
                        <div style={{ color: '#aaa', fontSize: 18 }}>{isOpen ? '▲' : '▼'}</div>
                      </div>
                      <div style={{ padding: '0 18px', borderTop: '1px solid #f5f5f5' }}>
                        <Timeline job={job} />
                      </div>
                      {isOpen && (
                        <div style={{ borderTop: '1px solid #f0f0f0', padding: '16px 18px', background: '#fafafa' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                            <div>
                              <SectionTitle>Job Details</SectionTitle>
                              <InfoRow label="Contract Date" value={job.contract_date || '—'} />
                              <InfoRow label="Contract Amt" value={formatCurrency(job.contract_amt)} />
                              {job.change_order_amt && <InfoRow label="Change Order" value={formatCurrency(job.change_order_amt)} />}
                              <InfoRow label="Prod Status" value={job.prod_status || '—'} />
                            </div>
                            <div>
                              <SectionTitle>Scheduling</SectionTitle>
                              <InfoRow label="Start Date" value={job.start_date || '—'} />
                              <InfoRow label="End Date" value={job.end_date || '—'} />
                              <InfoRow label="Permit" value={job.permit || '—'} />
                              <InfoRow label="Cert Ready" value={job.cert_ready || '—'} />
                            </div>
                            <div>
                              <SectionTitle>Contact</SectionTitle>
                              <InfoRow label="Phone" value={job.phone || '—'} />
                              <InfoRow label="Address" value={job.address || '—'} />
                              <InfoRow label="City" value={job.city || '—'} />
                            </div>
                          </div>
                          {job.notes && <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff8e1', borderRadius: 6, fontSize: 13 }}><strong>Notes:</strong> {job.notes}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'commission' && (
          <>
            {/* Commission summary */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
              <SummaryCard label="Total Earned" value={fmt(totalEarned)} color="#0d1f3b" sub="All completed jobs" />
              <SummaryCard label="Total Paid" value={fmt(totalPaid)} color="#1a355f" sub="In processed paychecks" />
              <SummaryCard label="Pending" value={fmt(totalPending)} color="#F57F17" sub="Awaiting next paycheck" />
              <SummaryCard label="Commission Rate" value={isManager(displayName) ? '3% own / override' : (rate * 100).toFixed(2) + '%'} color="#1565C0" sub={isManager(displayName) ? '3% own sales + team override' : 'Your rate'} />
            </div>

            {/* Manager override section */}
            {isManager(displayName) && overrideData && (
              <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20, overflow: 'hidden' }}>
                <div style={{ padding: '12px 18px', background: '#1565C0', color: '#fff', fontWeight: 700, fontSize: 14 }}>
                  👑 Manager Override — {(overrideData.overrideRate * 100).toFixed(1)}% on Team Sales
                </div>
                <div style={{ padding: 16, display: 'flex', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ background: '#e3f2fd', borderRadius: 8, padding: '10px 16px', minWidth: 140 }}>
                    <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', marginBottom: 4 }}>Total Override</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#1565C0' }}>{fmt(overrideData.totalOverride)}</div>
                  </div>
                  <div style={{ background: '#c8d3e6', borderRadius: 8, padding: '10px 16px', minWidth: 140 }}>
                    <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', marginBottom: 4 }}>Paid Override</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#1a355f' }}>{fmt(overrideData.paidOverride)}</div>
                  </div>
                  <div style={{ background: '#fff3e0', borderRadius: 8, padding: '10px 16px', minWidth: 140 }}>
                    <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', marginBottom: 4 }}>Pending Override</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#F57F17' }}>{fmt(overrideData.pendingOverride)}</div>
                  </div>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9f9f9', borderBottom: '1px solid #eee' }}>
                      <th style={{ padding: '8px 16px', textAlign: 'left', color: '#888' }}>Customer</th>
                      <th style={{ padding: '8px 16px', textAlign: 'left', color: '#888' }}>Canvasser</th>
                      <th style={{ padding: '8px 16px', textAlign: 'right', color: '#888' }}>Job Value</th>
                      <th style={{ padding: '8px 16px', textAlign: 'right', color: '#888' }}>Override</th>
                      <th style={{ padding: '8px 16px', textAlign: 'center', color: '#888' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrideData.entries.map(e => (
                      <tr key={e.id} style={{ borderBottom: '1px solid #f5f5f5', background: e.paid ? '#f9fbff' : '#fff' }}>
                        <td style={{ padding: '10px 16px', fontWeight: 600 }}>{e.customer_name}</td>
                        <td style={{ padding: '10px 16px', color: '#555' }}>{e.staff_name || e.canvasser || '—'}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>{fmt(e.job_value)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: e.paid ? '#1a355f' : '#F57F17' }}>{fmt(e.override_amt)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                          <span style={{ background: e.paid ? '#e8edf5' : '#fff3e0', color: e.paid ? '#1a355f' : '#F57F17', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                            {e.paid ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pending jobs */}
            {pendingJobs.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20, overflow: 'hidden' }}>
                <div style={{ padding: '12px 18px', background: '#F57F17', color: '#fff', fontWeight: 700, fontSize: 14 }}>
                  ⏳ Pending Commission — Awaiting Next Paycheck
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#fff8e1', borderBottom: '1px solid #eee' }}>
                      <th style={{ padding: '8px 16px', textAlign: 'left', color: '#888' }}>Customer</th>
                      <th style={{ padding: '8px 16px', textAlign: 'left', color: '#888' }}>PO#</th>
                      <th style={{ padding: '8px 16px', textAlign: 'right', color: '#888' }}>Job Value</th>
                      <th style={{ padding: '8px 16px', textAlign: 'right', color: '#888' }}>Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingJobs.map(job => (
                      <tr key={job.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '10px 16px', fontWeight: 600 }}>{job.customer_name}</td>
                        <td style={{ padding: '10px 16px', color: '#888' }}>{job.po_number}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>{fmt(jobValue(job))}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: '#F57F17' }}>{fmt(jobValue(job) * rate)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#fff8e1' }}>
                      <td colSpan={3} style={{ padding: '10px 16px', fontWeight: 700 }}>Total Pending</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: '#F57F17', fontSize: 15 }}>{fmt(totalPending)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Pay history */}
            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 18px', background: '#1a355f', color: '#fff', fontWeight: 700, fontSize: 14 }}>
                ✅ Paycheck History
              </div>
              {payrollLoading ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</div>
              ) : payrollHistory.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No paychecks processed yet</div>
              ) : payrollHistory.map(period => (
                <div key={period.pay_date} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ padding: '12px 16px', background: '#f9f9f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 700, color: '#0d1f3b' }}>Pay Date: {period.pay_date}</div>
                    <div style={{ fontWeight: 700, color: '#1a355f', fontSize: 15 }}>{fmt(period.myTotal)}</div>
                  </div>
                  {period.entries.map(e => (
                    <div key={e.id} style={{ padding: '8px 16px 8px 28px', display: 'flex', justifyContent: 'space-between', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}>
                      <span style={{ color: '#555' }}>{e.customer_name} · {e.po_number}</span>
                      <span style={{ color: '#1a355f', fontWeight: 600 }}>{fmt(e.commission_amt)}</span>
                    </div>
                  ))}
                </div>
              ))}
              {payrollHistory.length > 0 && (
                <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, borderTop: '2px solid #1a355f' }}>
                  <span>Total All-Time</span>
                  <span style={{ color: '#1a355f' }}>{fmt(totalPaid)}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function SummaryCard({ label, value, color, sub }) {
  return (
    <div style={{ background: '#fff', border: `2px solid ${color}`, borderRadius: 8, padding: '10px 18px', minWidth: 130 }}>
      <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{children}</div>;
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}>
      <span style={{ color: '#888' }}>{label}</span>
      <span style={{ fontWeight: 600, color: '#333' }}>{value}</span>
    </div>
  );
}
